import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Error Log Service for remote debugging and error tracking
 * Enables logging frontend errors to backend and viewing error history
 */

// Error log interfaces
export interface ErrorLog {
  id: number;
  timestamp: string;
  level: string; // Info, Warning, Error, Critical
  source: string;
  message: string;
  stackTrace?: string;
  userId?: string;
  userAgent?: string;
  ipAddress?: string;
  requestPath?: string;
  requestMethod?: string;
  errorCode?: string;
  additionalData?: string;
}

export interface ErrorStatistics {
  [level: string]: number;
}

export interface FrontendErrorRequest {
  source: string;
  message: string;
  stackTrace?: string;
  additionalData?: string;
}

export interface ApiResponse<T> {
  retcode: number;
  message: string;
  data?: T;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLogService {
  private readonly apiUrl = `${environment.apiUrl}/error-logs`;

  constructor(private http: HttpClient) {}

  /**
   * Get recent error logs with optional filtering
   * @param count Number of logs to retrieve (max 500, default 100)
   * @param level Filter by level (Info, Warning, Error, Critical)
   * @param source Filter by source (component/controller name)
   */
  getRecentErrors(
    count: number = 100,
    level?: string,
    source?: string
  ): Observable<ErrorLog[]> {
    let params: any = { count };
    if (level) params.level = level;
    if (source) params.source = source;

    return this.http.get<ApiResponse<ErrorLog[]>>(this.apiUrl, { params }).pipe(
      map(response => response.data || []),
      catchError(this.handleError)
    );
  }

  /**
   * Get error details by ID
   * @param id Error log ID
   */
  getErrorById(id: number): Observable<ErrorLog> {
    return this.http.get<ApiResponse<ErrorLog>>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        if (!response.data) {
          throw new Error('Error log not found');
        }
        return response.data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get error count statistics by level
   * @param since Optional date to count errors since (default: last 7 days)
   */
  getErrorStatistics(since?: Date): Observable<ErrorStatistics> {
    const params: any = {};
    if (since) {
      params.since = since.toISOString();
    }

    return this.http.get<ApiResponse<ErrorStatistics>>(`${this.apiUrl}/statistics`, { params }).pipe(
      map(response => response.data || {}),
      catchError(this.handleError)
    );
  }

  /**
   * Log error from frontend application
   * @param error Frontend error details
   */
  logFrontendError(error: FrontendErrorRequest): Observable<any> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/log-frontend-error`, error).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Clear old error logs (admin only)
   * @param daysToKeep Number of days of logs to keep (1-365)
   */
  clearOldLogs(daysToKeep: number = 30): Observable<any> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/clear-old`, { daysToKeep }).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Test error logging endpoint (admin only)
   */
  testErrorLogging(): Observable<any> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/test`, {}).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Helper method to log errors with automatic source detection
   * @param source Component or service name
   * @param error Error object or message
   */
  logError(source: string, error: Error | string): void {
    const errorRequest: FrontendErrorRequest = {
      source,
      message: typeof error === 'string' ? error : error.message,
      stackTrace: typeof error === 'string' ? undefined : error.stack,
      additionalData: typeof error === 'string' ? undefined : JSON.stringify({
        name: (error as Error).name,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      })
    };

    // Fire and forget - don't wait for response
    this.logFrontendError(errorRequest).subscribe({
      error: (err) => {
        // Fail silently to avoid error logging loop
        console.error('[ErrorLogService] Failed to log error:', err);
      }
    });
  }

  /**
   * Helper method to log warnings
   * @param source Component or service name
   * @param message Warning message
   */
  logWarning(source: string, message: string): void {
    this.logError(source, new Error(`WARNING: ${message}`));
  }

  /**
   * Helper method to log info messages
   * @param source Component or service name
   * @param message Info message
   */
  logInfo(source: string, message: string): void {
    this.logError(source, new Error(`INFO: ${message}`));
  }

  /**
   * Private error handler
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Backend error
      errorMessage = error.error?.message || `Server error: ${error.status}`;
    }

    console.error('[ErrorLogService]', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Format error log for display
   * @param errorLog Error log to format
   */
  formatErrorLog(errorLog: ErrorLog): string {
    const timestamp = new Date(errorLog.timestamp).toLocaleString();
    const header = `[${errorLog.level}] ${timestamp} - ${errorLog.source}`;
    const message = errorLog.message;
    const stackTrace = errorLog.stackTrace ? `\n\nStack Trace:\n${errorLog.stackTrace}` : '';

    return `${header}\n${message}${stackTrace}`;
  }

  /**
   * Get error log level badge class for UI
   * @param level Error level
   */
  getLevelBadgeClass(level: string): string {
    switch (level.toLowerCase()) {
      case 'info':
        return 'badge bg-info';
      case 'warning':
        return 'badge bg-warning';
      case 'error':
        return 'badge bg-danger';
      case 'critical':
        return 'badge bg-dark';
      default:
        return 'badge bg-secondary';
    }
  }

  /**
   * Get error log level icon for UI
   * @param level Error level
   */
  getLevelIcon(level: string): string {
    switch (level.toLowerCase()) {
      case 'info':
        return 'bi-info-circle';
      case 'warning':
        return 'bi-exclamation-triangle';
      case 'error':
        return 'bi-x-circle';
      case 'critical':
        return 'bi-x-octagon';
      default:
        return 'bi-question-circle';
    }
  }
}
